// src/hooks/useSettingsInit.ts
// Loads user profile and applies UI settings (theme, locale, density, accent) at app boot.
// Cached settings are applied synchronously before first paint to prevent FOUC.

import { useEffect, useLayoutEffect, useRef } from 'react';
import { authStore, userprofileStore, userprofileSettingStore, themeStore, i18nStore } from '@core/stores';
import { applyTheme as applyColorTheme, loadActiveThemeId } from '../features/theme/logic';
import { hexToHsl, accentForeground } from '../features/userprofile/logic';
import type { ThemeMode } from '@core/stores';
import type { Locale } from '@core/stores';

// ============================================
// localStorage cache for UI settings
// ============================================

const UI_SETTINGS_CACHE_KEY = 'app_ui_settings';

function getCachedSettings(): Record<string, string> | null {
  try {
    const raw = localStorage.getItem(UI_SETTINGS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveCachedSettings(settings: Record<string, string>): void {
  try {
    localStorage.setItem(UI_SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Apply a settings map to the DOM/stores.
 * Works for both cached (eager) and fresh (API) settings.
 */
function applySettings(s: Record<string, string>): void {
  // Apply theme mode
  if (s.theme) {
    themeStore.setMode(s.theme as ThemeMode);
  }

  // Apply color theme
  const colorTheme = s.color_theme ?? loadActiveThemeId();
  applyColorTheme(colorTheme);

  // Apply compact mode
  if (s.compact_mode === 'true') {
    document.documentElement.classList.add('compact');
  } else {
    document.documentElement.classList.remove('compact');
  }

  // Apply language
  if (s.language) {
    i18nStore.setLocale(s.language as Locale).catch(() => {});
  }

  // Apply accent color
  if (s.accent_color) {
    const hsl = hexToHsl(s.accent_color);
    const fg = accentForeground(hsl);
    themeStore.setColors({
      light: { primary: hsl, primaryForeground: fg, ring: hsl },
      dark: { primary: hsl, primaryForeground: fg, ring: hsl },
    });
  }
}

/**
 * Initializes user profile and applies saved UI settings on app boot.
 * Should be called once in AppShell (wrapped with observer()).
 * Reads MobX observables during render so re-renders when auth becomes available.
 *
 * Uses useLayoutEffect to apply cached settings synchronously before the browser
 * paints, preventing a flash of default styling (FOUC). The API fetch runs in
 * useEffect and updates the cache for subsequent page loads.
 */
export function useSettingsInit() {
  const initialized = useRef(false);
  const cachedApplied = useRef(false);
  // Read observables during render — MobX (via observer) tracks these
  const isAuthenticated = authStore.isAuthenticated;
  const userId = authStore.userId;

  // Apply cached settings synchronously before first paint
  useLayoutEffect(() => {
    if (cachedApplied.current) return;
    cachedApplied.current = true;

    const cached = getCachedSettings();
    if (cached) {
      applySettings(cached);
    } else {
      // No cached settings yet — still apply color theme from its own localStorage key
      const themeId = loadActiveThemeId();
      if (themeId !== 'default') {
        applyColorTheme(themeId);
      }
    }
  }, []);

  // Fetch fresh settings from API and update cache
  useEffect(() => {
    if (initialized.current) return;
    if (!isAuthenticated || !userId) return;

    initialized.current = true;

    const loadAndApply = async () => {
      try {
        // Load default profile if not already loaded
        if (!userprofileStore.currentItem) {
          await userprofileStore.GetUserprofileDefault(userId);
        }

        const profileId = userprofileStore.currentItem?.entity?.id;
        if (!profileId) return;

        // Load UI settings from backend
        const resp = await userprofileSettingStore.GetUISettings(profileId);
        if (!resp.settings) return;

        // Cache for next page load
        saveCachedSettings(resp.settings);

        // Apply (may override cached values if they changed on another device)
        applySettings(resp.settings);
      } catch {
        // Settings init is best-effort — don't block app boot
      }
    };

    loadAndApply();
  }, [isAuthenticated, userId]);
}
